/**
 * Magnet URI parser (RFC 7191 subset).
 *
 * The canonical URI scheme looks like:
 *   magnet:?xt=urn:btih:<infohash>&dn=<name>&tr=<tracker>&tr=...
 *
 * We only need a few well-known parameters: `xt` (exact topic — includes the
 * bitTorrent info hash), `dn` (display name), and `tr` (tracker — may repeat).
 *
 * This is intentionally tolerant: BTIH can be base32 (32 chars) or hex (40
 * chars). We accept either, case-insensitive, and pass the cleaned value back
 * to the caller so the backend can decide which form it prefers.
 */

export interface ParsedMagnet {
  infoHash: string
  displayName: string
  trackers: string[]
  /** True when the input was a valid magnet URI we can act on. */
  valid: boolean
}

/**
 * Parse a magnet URI string into its component parts.
 *
 * Returns an object with `valid: false` (and empty fields) if the input is
 * not a recognizable magnet URI, so callers can use the parser as a
 * non-throwing validator.
 */
export function parseMagnet(input: string): ParsedMagnet {
  const empty: ParsedMagnet = { infoHash: '', displayName: '', trackers: [], valid: false }
  if (!input || typeof input !== 'string') return empty
  const trimmed = input.trim()
  // Browsers accept `magnet:?xt=...`; in practice prefixes may include a BOM
  // or stray whitespace, so we strip and re-test rather than rejecting hard.
  if (!/^magnet:\?/i.test(trimmed)) return empty
  // Use the URL constructor when supported — degrades to a manual parse on
  // odd environments (e.g. older WebViews).
  let params: URLSearchParams | null = null
  try {
    params = new URLSearchParams(trimmed.slice(trimmed.indexOf('?')))
  } catch {
    return empty
  }
  if (!params) return empty

  // xt=urn:btih:<HASH>
  const xtRaw = (params.get('xt') ?? '').trim()
  const xt = xtRaw.replace(/^urn:btih:/i, '').trim()
  let infoHash = ''
  if (/^[a-f0-9]{40}$/i.test(xt)) {
    infoHash = xt.toLowerCase()
  } else if (/^[a-z2-7]{32}$/i.test(xt)) {
    infoHash = xt.toLowerCase()
  }
  if (!infoHash) return empty

  const displayName = (params.get('dn') ?? '').trim()
  const trackers: string[] = []
  for (const tr of params.getAll('tr')) {
    const t = tr.trim()
    if (t) trackers.push(t)
  }

  return {
    infoHash,
    displayName,
    trackers,
    valid: true,
  }
}

/**
 * Build a magnet URL from an info hash (handy for "open externally" / copy).
 * Returns null when the input doesn't look like a usable hash.
 */
export function buildMagnetUrl(infoHash: string, displayName = ''): string | null {
  if (!/^[a-f0-9]{40}$/i.test(infoHash) && !/^[a-z2-7]{32}$/i.test(infoHash)) {
    return null
  }
  const u = new URL('magnet:?xt=urn:btih:' + infoHash.toLowerCase())
  if (displayName) u.searchParams.set('dn', displayName)
  return u.toString()
}
